import TimeKeeper from "../engine/TimeKeeper";
import CanvasStageModel from '../canvas/CanvasStageModel';
import GameController, { GAME_EVENTS } from '../game/GameController';

/**
 * Represents a drone attack and its attributes, such as position, radius, and movement.
 */
class DroneAttackModel {
    /**
     * Initializes a new instance of the DroneAttackModel.
     * 
     * @param {Object} parameters - The parameters object
     * @param {number} parameters.x - The x-coordinate of the drone attack
     * @param {number} parameters.y - The y-coordinate of the drone attack
     * @param {number} parameters.radius - The radius of the drone attack
     * @param {string} parameters.aircraftId - The id of the aircraft
     * @param {boolean} parameters.isMoving - Whether the drone attack is moving or not
     * @param {number} parameters.movingSpeed - The speed of the drone attack
     * @param {number} parameters.heading - The heading of the drone attack
     * @param {number} parameters.extraDespawnTime - The extra time to wait before despawning the drone
     */
    constructor(parameters) {
        this._x = parameters.x;
        this._y = parameters.y;
        this._radius = parameters.radius;
        this._aircraftId = parameters.aircraftId;
        this._isMoving = parameters.isMoving;
        this._movingSpeed = parameters.movingSpeed;
        this._heading = parameters.heading;
        this._extraDespawnTime = parameters.extraDespawnTime;

        this.shouldBeRemoved = false; // Flag to indicate if the drone should be removed
        this.timeToEnd = null;        // Time when removal is completed
    }

    // Getters for drone properties
    getX() { return this._x; }
    getY() { return this._y; }
    getRadius() { return this._radius; }
    getId() { return this._aircraftId; }
    getIsMoving() { return this._isMoving; }

    /**
     * Marks the drone for removal and calculates the removal time.
     */
    startRemoval() {
        this.shouldBeRemoved = true;
        this.timeToEnd = TimeKeeper.accumulatedDeltaTime + this._extraDespawnTime;
    }

     /**
     * Updates the drone's position based on its heading and speed.
     */
    moveDrone() {
        const deltaTime = TimeKeeper.getDeltaTimeForGameStateAndTimewarp();
        const speedFactor = 0.007 * this._movingSpeed * deltaTime;

        switch (this._heading) {
            case 1: // North West
                this._x -= speedFactor;
                this._y += speedFactor;
                break;
            case 2: // North East
                this._x += speedFactor;
                this._y += speedFactor;
                break;
            case 3: // South West
                this._x -= speedFactor;
                this._y -= speedFactor;
                break;
            case 4: // South East
                this._x += speedFactor;
                this._y -= speedFactor;
                break;
        }
    }
}

/**
 * Manages drone attacks and their interactions with aircraft in the airspace.
 */
class DroneAttackController {
    constructor() {
        this._droneAttacks = []; // List of active drone attacks
    }

    /**
     * Updates the state of all active drone attacks and checks interactions with aircraft.
     * 
     * @param {Object} aircraftModel - The model of the aircraft being monitored.
     */
    update(aircraftModel) {
        const droneAttacks = this.getDroneAttacks();

        for (let i = 0; i < droneAttacks.length; i++) {
            const droneAttack = droneAttacks[i];

            // Handle drone removal
            if (droneAttack.shouldBeRemoved) {
                if (TimeKeeper.accumulatedDeltaTime >= droneAttack.timeToEnd) {
                    droneAttacks.splice(i, 1);
                    i--;
                    continue;
                }
            }

            const aircraftPosition = CanvasStageModel.calculatePreciseCanvasPositionFromRelativePosition([
                aircraftModel.positionModel.relativePosition[0],
                aircraftModel.positionModel.relativePosition[1]
            ]);
            const dronePosition = CanvasStageModel.calculatePreciseCanvasPositionFromRelativePosition(
                [droneAttack.getX(), droneAttack.getY()]
            );
            const radiusInPixels = CanvasStageModel._translateKilometersToPixels(droneAttack.getRadius());

            if (!aircraftPosition || !dronePosition) return;

            // Check if aircraft is within the restricted drone area
            const distanceSquared = (aircraftPosition[0] - dronePosition[0]) ** 2 +
                                    (aircraftPosition[1] - dronePosition[1]) ** 2;

            if ((distanceSquared <= radiusInPixels ** 2) && !aircraftModel.airspaceViolation) {
                aircraftModel.airspaceViolation = true;
                console.log((aircraftModel.airlineId + aircraftModel.flightNumber).toUpperCase(), "is in restricted drone area!");
                GameController.events_recordNew(GAME_EVENTS.DRONE_AIRSPACE_VIOLATION);
            }

            // Handle drone movement or waypoint intersections
            if (droneAttack.getIsMoving()) {
                droneAttack.moveDrone();
            } else if (!aircraftModel.routeInRestrictedAirspace) {
                const waypoints = aircraftModel.fms.waypoints;

                // Check intersection between aircraft and first waypoint
                if (waypoints[0].positionModel === null) {
                    return;
                }

                const firstWaypointPosition = CanvasStageModel.calculatePreciseCanvasPositionFromRelativePosition([
                    waypoints[0].positionModel.relativePosition[0],
                    waypoints[0].positionModel.relativePosition[1]
                ]);

                // Check if the line segment between the aircraft and the first waypoint intersects the drone circle
                if (this._lineIntersectsCircle(aircraftPosition, firstWaypointPosition, dronePosition, radiusInPixels)) {
                    aircraftModel.routeInRestrictedAirspace = true;
                }

                // Check intersection for all waypoints
                for (let j = 0; j < waypoints.length - 1; j++) {
                    let waypoint = waypoints[j];
                    let nextWaypoint = waypoints[j + 1];

                    if (waypoint.positionModel === null || nextWaypoint.positionModel === null) {
                        return;
                    }

                    const waypointPosition = CanvasStageModel.calculatePreciseCanvasPositionFromRelativePosition([
                        waypoint.positionModel.relativePosition[0],
                        waypoint.positionModel.relativePosition[1]
                    ]);
                    const nextWaypointPosition = CanvasStageModel.calculatePreciseCanvasPositionFromRelativePosition([
                        nextWaypoint.positionModel.relativePosition[0],
                        nextWaypoint.positionModel.relativePosition[1]
                    ]);

                    // Check if the line segment between the two waypoints intersects the drone circle
                    if (this._lineIntersectsCircle(waypointPosition, nextWaypointPosition, dronePosition, radiusInPixels)) {
                        aircraftModel.routeInRestrictedAirspace = true;
                    }
                }
            }
        }
    }

    /**
     * Determines if a line segment intersects a circle.
     * 
     * @param {Array} p1 - The start point of the line segment.
     * @param {Array} p2 - The end point of the line segment.
     * @param {Array} center - The center of the circle.
     * @param {number} radius - The radius of the circle.
     * @returns {boolean} - True if the line intersects the circle, otherwise false.
     */
    _lineIntersectsCircle(p1, p2, center, radius) {
        const [x1, y1] = p1;
        const [x2, y2] = p2;
        const [cx, cy] = center;

        const dx = x2 - x1;
        const dy = y2 - y1;
        const fx = x1 - cx;
        const fy = y1 - cy;

        const a = dx * dx + dy * dy;
        const b = 2 * (fx * dx + fy * dy);
        const c = (fx * fx + fy * fy) - radius * radius;

        let discriminant = b * b - 4 * a * c;
        if (discriminant < 0) {
            return false;
        }

        discriminant = Math.sqrt(discriminant);

        const t1 = (-b - discriminant) / (2 * a);
        const t2 = (-b + discriminant) / (2 * a);

        if (t1 >= 0 && t1 <= 1 || t2 >= 0 && t2 <= 1) {
            return true;
        }

        return false;
    }

    setDroneAttacks(attacks) {
        this._droneAttacks = attacks;
    }

    getDroneAttacks() {
        return this._droneAttacks;
    }

    /**
     * Initiates the removal of a specific drone attack.
     * 
     * @param {string} aircraftId - The ID of the aircraft associated with the drone attack.
     */
    startRemovalOfDroneAttack(aircraftId)  {
        const droneAttacks = this.getDroneAttacks();
        for (let i = 0; i < droneAttacks.length; i++) {
            const droneAttack = droneAttacks[i];
            if (droneAttack.getId() == aircraftId) {
                droneAttack.startRemoval();
                break;
            }
        }
    }
}

export { DroneAttackController, DroneAttackModel };